import React, { useEffect, useMemo, useRef, useState, useContext, useCallback } from 'react';

// Styles & Assets
import styles from './ConfigureRoute.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Context
import GeneralContext from '../../../context/GeneralContext';

// Hooks
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// Components
import StopsRoute from '../StopsRoute/StopsRoute';
import RouteReadyDialog from './../RouteReadyDialog/RouteReadyDialog';
import Modal from '../../UI/Modal/Modal';
import GoogleMapReact from 'google-map-react';

// Interfaces
import { Route } from '../../../interfaces/routes';
import { Location } from '../../../interfaces/Location';

// Language
import { useTranslation } from 'react-i18next';

// Other
import { Link } from 'react-router-dom';
import { Patient } from '../../../interfaces/Patient';
import { createOrderedMarkers, fetchGeolocationAddress, getMapsRoute, renderRouteOnMap } from '../../../services/getRoute';
import { getAddress, getFullName } from '../../../services/getPatientAtribute';

interface IPatientWithLocation {
    patient: Patient;
    location: Location;
}

const ConfigureRoute: React.FC<{
    route: Route;
    userLocation: Location;
}> = ({ route, userLocation }) => {
    const defaultRouteVisits = route.visits;

    const { hospitalLocation, GOOGLE_API_KEY } = useContext(GeneralContext);

    const mapRef = useRef<google.maps.Map | null>(null);

    const [showDialogRouteReady, setShowModalRouteReady] = useState<boolean>(false);
    const [manualWaypointsOrder, setManualWaypointsOrder] = useState<number[] | null>(null);
    const [autoWaypointsOrder, setAutoWaypointsOrder] = useState<number[]>([]);

    const { t } = useTranslation();

    const { patientsWithWaypoints, createRoute } = useRouteData(route, GOOGLE_API_KEY);

    const initialWaypointsOrder = useMemo(() => {
        if (!defaultRouteVisits || patientsWithWaypoints.length === 0) return [];

        return defaultRouteVisits
            .map(v => patientsWithWaypoints.findIndex(p => p.patient.id === v.patient_id))
            .filter(index => index >= 0);
    }, [defaultRouteVisits, patientsWithWaypoints]);

    const addresses = useMemo(() => {
        return patientsWithWaypoints.map(p => p.location);
    }, [patientsWithWaypoints]);

    useEffect(() => {
        if (defaultRouteVisits) return;
        if (manualWaypointsOrder !== null) return;
        if (addresses.length === 0) return;

        getMapsRoute(userLocation, addresses, hospitalLocation)
            .then((result) => {
                const order = result.routes[0].waypoint_order;
                setAutoWaypointsOrder(order);
            });
    }, [addresses, defaultRouteVisits, hospitalLocation, manualWaypointsOrder, userLocation]);

    const waypointsOrder = useMemo(() => {
        if (manualWaypointsOrder !== null) return manualWaypointsOrder;
        if (defaultRouteVisits) return initialWaypointsOrder;
        return autoWaypointsOrder;
    }, [autoWaypointsOrder, defaultRouteVisits, initialWaypointsOrder, manualWaypointsOrder]);

    const sortedPatientsIDs = waypointsOrder.map(index => patientsWithWaypoints?.[index]?.patient.id);

    const reorderWaypoints = (indexS: number, indexD: number) => {
        setManualWaypointsOrder(oldWaypoints => {
            const source = oldWaypoints ?? waypointsOrder;
            const newWaypoints = [...source];
            const [reorderVisit] = newWaypoints.splice(indexS, 1);
            newWaypoints.splice(indexD, 0, reorderVisit);
            return newWaypoints;
        });
    };

    const handleDeleteWaypoint = (index: number) => {
        setManualWaypointsOrder(oldWaypoints => {
            const source = oldWaypoints ?? waypointsOrder;
            const newWaypoints = [...source];
            newWaypoints.splice(index, 1);
            return newWaypoints;
        });
    };

    const sortedWaypoints = useMemo(() => {
        return waypointsOrder.map(index => addresses[index]);
    }, [addresses, waypointsOrder]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || waypointsOrder.length === 0) return;

        let directionsRenderer: google.maps.DirectionsRenderer | undefined;
        let markers: google.maps.Marker[] = [];

        getMapsRoute(userLocation, sortedWaypoints, hospitalLocation).then((result) => {
            directionsRenderer = renderRouteOnMap(map, result);
            markers = createOrderedMarkers(map, userLocation, sortedWaypoints, hospitalLocation);
        });

        return () => {
            directionsRenderer?.setMap(null);
            markers.forEach(m => m?.setMap(null));
        };
    }, [waypointsOrder, sortedWaypoints, userLocation, hospitalLocation]);

    const routeLocations = [userLocation, ...sortedWaypoints, hospitalLocation];
    const sortedPatients = waypointsOrder.map(index => patientsWithWaypoints?.[index]?.patient);

    const handleConfirm = () => {
        createRoute({
            patients: sortedPatientsIDs,
            start_from_hospital: userLocation.lat === hospitalLocation.lat && userLocation.lng === hospitalLocation.lng
        }, () => setShowModalRouteReady(true));
    };

    return (
        <div className={styles.configureRouteContainer}>
            <h1 className={styles.title}>{route.name}</h1>

            <div className={styles.configureRoute}>
                <GoogleMapReact
                    bootstrapURLKeys={{ key: GOOGLE_API_KEY }}
                    defaultZoom={10}
                    center={userLocation}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({ map }) => { mapRef.current = map; }}
                />
                <StopsRoute
                    sortedPatients={sortedPatients}
                    reorderWaypoints={reorderWaypoints}
                    handleDeleteWaypoint={handleDeleteWaypoint}
                />
            </div>

            <div className={styles.buttonsContainer}>
                <Link
                    to={'/home/routes/routes_map_patients'}
                    className={styles.buttonCancel}
                >
                    {t('cancel')}
                </Link>
                <button onClick={handleConfirm} className={styles.buttonConfirm}>
                    {t('generate_route')}
                </button>
            </div>

            <Modal show={showDialogRouteReady} setShowModal={setShowModalRouteReady}>
                <RouteReadyDialog
                    setModalOpened={setShowModalRouteReady}
                    addresses={routeLocations}
                />
            </Modal>
        </div>
    );
};

export default ConfigureRoute;

const useRouteData = (route: Route, GOOGLE_API_KEY: string) => {
    const [patientsWithWaypoints, setPatientsWithWaypoints] = useState<IPatientWithLocation[]>([]);

    const { error } = useToast();
    const { t } = useTranslation();

    const { sendRequest: postRoute } = useHttp();
    const { sendRequest: getPatient } = useHttp<Patient>();

    const createRoute = ({ patients, start_from_hospital }: { patients: Patient['id'][]; start_from_hospital: boolean }, callback: () => void) => {
        const data = {
            name: route.name,
            date: new Date(Date.now()).toISOString(),
            patients: patients,
            start_from_hospital: start_from_hospital,
            id: route.id ?? undefined
        };

        postRoute({
            url: getEnvironmentApiUrl() + 'route/',
            method: route.id ? 'PUT' : 'POST',
            body: route.id
                ? { filter: { id: route.id }, data }
                : data,
        }, callback);
    };

    useEffect(() => {
        const patients = route.patients;
        if (!patients) return;

        let madeCalls = 0;
        const patientsLocation: { patient: Patient; location: Location }[] = [];

        const callbackMultiple = () => {
            madeCalls++;
            if (madeCalls < patients.length) return;
            setPatientsWithWaypoints(patientsLocation);
        };

        patients.forEach((patientID, index) => {
            getPatient({
                url: getEnvironmentApiUrl() + 'patient/' + patientID
            }, async (data) => {
                const geolocation = await fetchGeolocationAddress(getAddress(data), GOOGLE_API_KEY);

                if (geolocation.results.length > 0) {
                    patientsLocation[index] = {
                        patient: data,
                        location: {
                            lat: geolocation.results[0].geometry.location.lat,
                            lng: geolocation.results[0].geometry.location.lng,
                        }
                    };
                } else {
                    error(`${t('patient_location_not_found')} ${getFullName(data)}. ${data.addresses.length === 0 ? t('please_add_address') : ''}`);
                }

                callbackMultiple();
            });
        });
    }, [GOOGLE_API_KEY, t, error, getPatient, route.patients]);

    return {
        patientsWithWaypoints,
        createRoute
    };
};